import { useState } from "react";
import { Link } from "react-router-dom";
import { Section, Card } from "@/components/Section";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { submitConsultation } from "@/lib/api";
import { SITE } from "@/lib/site";
import { CheckCircle2, Phone, Calendar, Mail } from "lucide-react";

const INDUSTRIES = ["K–12 Education", "Higher Education", "Corporate", "Small Business", "Nonprofit", "Financial / Professional Services", "Real Estate", "Association / Chamber", "Other"];
const SERVICES = ["AI Consulting", "Training or Workshop", "Conference Presentation or Keynote", "AI Agent or Super-Agent Development", "Workflow Design", "Website Development", "PowerPoint or Presentation Design", "Research and Executive Brief", "Custom Leadership Toolkit", "Other"];
const TIMELINES = ["Within 2 weeks", "Within 1 month", "1–3 months", "3–6 months", "Flexible / Exploring"];
const FORMATS = ["Virtual", "In-person", "Unsure"];
const CONTACTS = ["Email", "Phone", "Either"];

const initial = {
  first_name: "", last_name: "", organization: "", job_title: "",
  email: "", phone: "", industry: "", service_of_interest: "",
  project_description: "", primary_goal: "", preferred_timeline: "",
  number_of_participants: "", preferred_format: "", event_date: "",
  budget_range: "", preferred_contact_method: "", heard_about: "", consent: false,
};

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const needsParticipants = ["Training or Workshop", "Conference Presentation or Keynote"].includes(form.service_of_interest);
  const needsEventDate = ["Training or Workshop", "Conference Presentation or Keynote"].includes(form.service_of_interest);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error("Please provide consent to submit this form."); return; }
    setSubmitting(true);
    try {
      await submitConsultation(form);
      setSubmitted(true);
      toast.success("Request received. We will be in touch.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="page-contact">
      <Section eyebrow="Book a Consultation" title="Tell us about your work. We'll take it from there."
        kicker="Every consultation begins with the work you already manage. Share a few details and we will respond promptly.">
        <div className="grid md:grid-cols-12 gap-8">
          <div className="md:col-span-4 space-y-4">
            <Card>
              <div className="eyebrow">Direct Contact</div>
              <ul className="mt-4 space-y-3 text-sm">
                <li className="flex items-center gap-3 text-[var(--layor-navy)]"><Phone size={16} className="text-[var(--layor-gold)]" /><a href={SITE.phoneHref} data-testid="contact-phone-link">{SITE.phone}</a></li>
                {SITE.email && <li className="flex items-center gap-3 text-[var(--layor-navy)]"><Mail size={16} className="text-[var(--layor-gold)]" /><a href={`mailto:${SITE.email}`}>{SITE.email}</a></li>}
                {SITE.calendly && <li className="flex items-center gap-3 text-[var(--layor-navy)]"><Calendar size={16} className="text-[var(--layor-gold)]" /><a href={SITE.calendly} target="_blank" rel="noreferrer" data-testid="contact-calendly">Schedule via Calendly</a></li>}
              </ul>
            </Card>
            {SITE.calendly && (
              <Card>
                <div className="eyebrow text-[var(--layor-blue)]">Prefer to book directly?</div>
                <p className="mt-3 text-sm text-[var(--layor-muted)]">Grab a time on Patricia's calendar.</p>
                <a href={SITE.calendly} target="_blank" rel="noreferrer" className="btn-gold mt-4 inline-flex rounded-full px-5 py-2.5 text-sm font-semibold">Open Calendly</a>
              </Card>
            )}
            <Card>
              <div className="eyebrow">Prefer a Quote Instead?</div>
              <p className="mt-3 text-sm text-[var(--layor-muted)]">If you know the service you need and want a customized quote, start there.</p>
              <Link to="/quote" className="btn-ghost mt-4 inline-flex rounded-full px-5 py-2.5 text-sm font-semibold">Request a Quote</Link>
            </Card>
          </div>

          <div className="md:col-span-8">
            {submitted ? (
              <Card data-testid="contact-success">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[var(--layor-gold)]/20 text-[var(--layor-navy)]"><CheckCircle2 size={22} /></div>
                <h3 className="mt-4 font-display text-2xl text-[var(--layor-navy)]">Thank you for contacting LAYOR Digital Group.</h3>
                <p className="mt-3 text-[var(--layor-muted)] leading-relaxed">
                  We have received your request and will review the information you provided. A member of our team will contact you to discuss your goals, answer any questions, and determine the appropriate next steps for your project.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link to="/" className="btn-primary rounded-full px-5 py-2.5 text-sm font-semibold">Back to Home</Link>
                  <Link to="/services" className="btn-ghost rounded-full px-5 py-2.5 text-sm font-semibold">Explore Services</Link>
                </div>
              </Card>
            ) : (
              <Card>
                <form onSubmit={onSubmit} className="grid sm:grid-cols-2 gap-5" data-testid="consultation-form">
                  <Field label="First name *"><Input required data-testid="consult-first-name" value={form.first_name} onChange={set("first_name")} /></Field>
                  <Field label="Last name *"><Input required data-testid="consult-last-name" value={form.last_name} onChange={set("last_name")} /></Field>
                  <Field label="Organization or company"><Input data-testid="consult-organization" value={form.organization} onChange={set("organization")} /></Field>
                  <Field label="Job title or role"><Input data-testid="consult-title" value={form.job_title} onChange={set("job_title")} /></Field>
                  <Field label="Email *"><Input type="email" required data-testid="consult-email" value={form.email} onChange={set("email")} /></Field>
                  <Field label="Phone (optional)"><Input data-testid="consult-phone" value={form.phone} onChange={set("phone")} /></Field>

                  <Field label="Industry">
                    <Select value={form.industry} onValueChange={set("industry")}>
                      <SelectTrigger data-testid="consult-industry"><SelectValue placeholder="Select industry" /></SelectTrigger>
                      <SelectContent>{INDUSTRIES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Service of interest">
                    <Select value={form.service_of_interest} onValueChange={set("service_of_interest")}>
                      <SelectTrigger data-testid="consult-service"><SelectValue placeholder="Select a service" /></SelectTrigger>
                      <SelectContent>{SERVICES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>

                  <Field label="Project or training description *" full>
                    <Textarea required rows={4} data-testid="consult-description" value={form.project_description} onChange={set("project_description")} placeholder="What are you trying to accomplish? What responsibilities is this connected to?" />
                  </Field>

                  <Field label="Primary goal" full>
                    <Textarea rows={3} data-testid="consult-goal" value={form.primary_goal} onChange={set("primary_goal")} />
                  </Field>

                  <Field label="Preferred timeline">
                    <Select value={form.preferred_timeline} onValueChange={set("preferred_timeline")}>
                      <SelectTrigger data-testid="consult-timeline"><SelectValue placeholder="Select timeline" /></SelectTrigger>
                      <SelectContent>{TIMELINES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Preferred format">
                    <Select value={form.preferred_format} onValueChange={set("preferred_format")}>
                      <SelectTrigger data-testid="consult-format"><SelectValue placeholder="Select format" /></SelectTrigger>
                      <SelectContent>{FORMATS.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>

                  {needsParticipants && (
                    <Field label="Number of participants"><Input data-testid="consult-participants" value={form.number_of_participants} onChange={set("number_of_participants")} placeholder="e.g., 30" /></Field>
                  )}
                  {needsEventDate && (
                    <Field label="Event date"><Input type="date" data-testid="consult-event-date" value={form.event_date} onChange={set("event_date")} /></Field>
                  )}

                  <Field label="Investment expectations (optional)"><Input data-testid="consult-budget" value={form.budget_range} onChange={set("budget_range")} placeholder="Prefer to discuss with LAYOR" /></Field>
                  <Field label="Preferred contact method">
                    <Select value={form.preferred_contact_method} onValueChange={set("preferred_contact_method")}>
                      <SelectTrigger data-testid="consult-contact-method"><SelectValue placeholder="Select preference" /></SelectTrigger>
                      <SelectContent>{CONTACTS.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>

                  <Field label="How did you hear about LAYOR Digital Group?" full>
                    <Input data-testid="consult-heard-about" value={form.heard_about} onChange={set("heard_about")} />
                  </Field>

                  <div className="sm:col-span-2 flex items-start gap-3 pt-2">
                    <Checkbox id="consult-consent" checked={form.consent} onCheckedChange={(v) => setForm((f) => ({ ...f, consent: !!v }))} data-testid="consult-consent" />
                    <Label htmlFor="consult-consent" className="text-sm text-[var(--layor-muted)] leading-relaxed">
                      I agree to be contacted about my inquiry and consent to my information being stored securely by LAYOR Digital Group.
                    </Label>
                  </div>

                  <div className="sm:col-span-2 flex flex-wrap items-center gap-3">
                    <button type="submit" disabled={submitting} className="btn-primary inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold disabled:opacity-60" data-testid="consult-submit">
                      {submitting ? "Sending..." : "Submit Request"}
                    </button>
                    <Link to="/quote" className="btn-ghost rounded-full px-5 py-3 text-sm font-semibold">Request a Quote Instead</Link>
                  </div>
                </form>
              </Card>
            )}
          </div>
        </div>
      </Section>
    </div>
  );
}

const Field = ({ label, children, full }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <Label className="text-xs uppercase tracking-[0.18em] text-[var(--layor-muted)]">{label}</Label>
    <div className="mt-2">{children}</div>
  </div>
);
